import React, { useContext } from 'react';
import Styles from './PurchaseGridCard.module.scss';
import { useTranslation } from 'react-i18next';
import { AuthContext } from 'components/lib';
import { AccessTime, ImportContacts } from '@mui/icons-material';
import Badge, { BADGE_TYPES } from 'components/global/CardBadge/CardBadge';
import { useMemo } from 'react';


const PurchaseGridCard = (props) => {
    const { t } = useTranslation();
    const authContext = useContext(AuthContext);

    const prices = props?.data?.pricing;
    const currencySymbol = authContext?.user.currencySymbol;

    const courseCount = props?.data?.labCount || 0;
    const moduleCount = props?.data?.nodeCount || 0;
    function getCertDuration(data) {
        const count = data?.labsData?.
            labs?.reduce((acc, labData) => {
                const originalLab = data?.labs?.labs?.find(lab => lab.lab_id === labData.lab_id);
                const multiplier = originalLab?.is_project ? 2 : 1;
                const nodeCount = labData?.nodeCount
                return acc + (nodeCount * multiplier)
            }, 0)
        return count
    }
    const durationHours = useMemo(() => {
        if (props?.type === "cert") {
            return getCertDuration(props.data);
        }

        const multiplier = props?.data?.is_project ? 2 : 1;
        return moduleCount * multiplier;
    }, [props.type, props.data, moduleCount]);
    const displayCount = props?.type === "cert" ? courseCount : moduleCount;
    const labelKey = props?.type === "cert" ? "course" : "modules";

    const plan = authContext?.user?.plan;

    const isHybrid = plan === 'hybrid';
    const isScholar = plan === 'free';
    const isPurchaseMode = props?.mode === 'purchase';
    const isEnrollMode = props?.mode === 'enroll';

    const shouldShowBadge = (() => {
        if ((isScholar || isHybrid) && isPurchaseMode) return true;

        if (isEnrollMode && !isHybrid) {
            return Boolean(props?.data?.association);
        }

        return false;
    })();


    const resolveBadgeConfig = (data) => {
        if (data?.association) {
            return {
                type: BADGE_TYPES.INFO,
                label: 'Certificate Exclusive',
            };
        }

        if (data?.renew) {
            return {
                type: BADGE_TYPES.WARN,
                label: 'Pending Renewal',
            };
        }

        if (props?.type === "internship") {
            return null;
        }

        return {
            type: BADGE_TYPES.SUCCESS,
            label: 'Free Trial',
        };
    };

    const badgeConfig = resolveBadgeConfig(props?.data, props?.type);

    return (
        <>
            <div
                data-testid="PurchaseGridCard-container"
                data-tooltip-id={isPurchaseMode ? "purchase_labs" : undefined}
                className={Styles.container}
                data-some-relevant-attr={props.data.id}
                onClick={() => { props?.data?.association ? props?.handleProductAssociation?.(props?.data?.association) : props?.onCardClick?.(props?.data) }}
            >
                <div data-testid="PurchaseGridCard-coursecard" className={Styles.coursecard}>

                    {/* ---------- HEADER ---------- */}
                    <div data-testid="PurchaseGridCard-cardheader" className={Styles.cardheader}>
                        {props?.showBadge && shouldShowBadge && badgeConfig && (
                            <Badge data-testid="PurchaseGridCard-badge" type={badgeConfig.type}>
                                {badgeConfig.label}
                            </Badge>
                        )}

                        <div data-testid="PurchaseGridCard-imagewrapper" className={Styles.imagewrapper}>
                            <img src={`${process.env.REACT_APP_GLOBAL_MEDIA_URL}${props?.data?.icon || props?.data?.image}`} alt="courseImg" />
                        </div>
                    </div>

                    {/* ---------- BODY ---------- */}
                    <div data-testid="PurchaseGridCard-cardbody" className={Styles.cardbody}>
                        <h3>{props?.data?.name || props?.data?.title}</h3>

                        {(props?.data?.description || props?.data?.descr) && (
                            <p className={Styles.description}>
                                {props?.data?.description || props?.data?.descr}
                            </p>
                        )}

                        {/* ---------- FOOTER ---------- */}
                        <div data-testid="PurchaseGridCard-cardbody-footer" className={`${Styles.footer} ${props?.type === "internship" ? Styles.footerInternship : ""
                            }`}>

                            {/* LEFT SIDE — ICON INFO */}
                            {(props?.type !== "internship") && (
                                <div data-testid="PurchaseGridCard-courseinfo" className={Styles.courseinfo}>
                                    <div data-testid="PurchaseGridCard-infoitem" className={Styles.infoitem}>
                                        <ImportContacts fontSize='small' sx={{ color: '#1450a0' }} />
                                        {displayCount} {t(`PurchaseCards.PurchaseGridCards.PurchaseGridCard.${labelKey}`)}
                                    </div>

                                    <div data-testid="PurchaseGridCard-infoitem1" className={Styles.infoitem}>
                                        <AccessTime fontSize='small' sx={{ color: '#1450a0' }} />
                                        {durationHours} {t('PurchaseCards.PurchaseGridCards.PurchaseGridCard.hours')}
                                    </div>
                                </div>
                            )}

                            <div data-testid="PurchaseGridCard-rightside" className={Styles.rightside}>

                                {/* PRICE BLOCK (ONLY IN PURCHASE MODE) */}
                                {isPurchaseMode && (
                                    <div data-testid="PurchaseGridCard-priceinfo" className={Styles.priceinfo}>
                                        <div data-testid="PurchaseGridCard-Mainpricewrapper"
                                            key={`${prices?.id}`}
                                            style={{ display: "flex", flexDirection: "column" }}
                                        >
                                            <div data-testid="PurchaseGridCard-Mainprice" className={Styles.Mainprice}>
                                                {currencySymbol}{prices?.price}
                                            </div>

                                            {prices?.actual_price && (
                                                <div data-testid="PurchaseGridCard-productPrice" id="productPrice" className={Styles.Strikeprice}>
                                                    <span
                                                        style={{
                                                            textDecoration: "line-through",
                                                            marginRight: "4px",
                                                            color: "gray",
                                                            fontSize: "14px"
                                                        }}
                                                    >
                                                        {currencySymbol}{prices?.actual_price}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* CHIP */}
                                <div data-testid="PurchaseGridCard-chip" className={Styles.chip}>{t('PurchaseCards.PurchaseGridCards.PurchaseGridCard.access', { months: prices?.interval })}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PurchaseGridCard;